import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { memberApi } from "../lib/memberApi";
import { COUNTRY_PHONE_OPTIONS, INDIA_COUNTRY_OPTION } from "../data/phoneCountries";

const MEMBER_DRAFT_PROFILE_KEY = "ospf_member_draft_profile";
const PHONE_REGEX = /^\d{5,15}$/;
const INDIAN_PHONE_REGEX = /^[6-9]\d{9}$/;
const cleanPhoneDigits = (value) => (value || "").toString().replace(/\D/g, "");

const saveMemberDraftProfile = (profile) => {
  try {
    localStorage.setItem(MEMBER_DRAFT_PROFILE_KEY, JSON.stringify(profile));
  } catch { }
};

export default function MemberLogin() {
  const nav = useNavigate();
  const location = useLocation();
  const { memberLogin } = useAuth();

  const [mode, setMode] = useState("login");
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(INDIA_COUNTRY_OPTION);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    confirm: "",
  });

  // =========================
  // Common Redirect Function
  // =========================
  const routeMember = useCallback((member) => {
    const status = member?.membership_status?.toLowerCase();

    if (status === "approved") {
      nav("/member/dashboard", { replace: true });
      return;
    }

    if (!status || status === "draft") {
      const resume = member?.id ? `?resume=${member.id}` : "";
      nav(`/membership${resume}`, { replace: true });
      return;
    }

    nav("/member/dashboard", { replace: true });
  }, [nav]);

  // =========================
  // Auto login if token exists
  // =========================
  useEffect(() => {
    const query = new URLSearchParams(location.search).get("mode");
    if (query === "register") {
      setMode("register");
    }
  }, [location.search]);

  useEffect(() => {
    const token = localStorage.getItem("ospf_member_token");

    if (!token) {
      setInitializing(false);
      return;
    }

    const boot = async () => {
      try {
        const { data } = await memberApi.get("/members/me");
        const member = data.member || data;

        routeMember(member);
      } catch {
        localStorage.removeItem("ospf_member_token");
      } finally {
        setInitializing(false);
      }
    };

    boot();
  }, [location.search, routeMember]);

  // =========================
  // Form Change
  // =========================
  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // =========================
  // Register
  // =========================
  const handleRegister = async () => {
    const rawPhone = cleanPhoneDigits(form.phone);

    if (!form.name.trim()) return toast.error("Enter full name");
    if (!rawPhone) return toast.error("Enter mobile number");

    if (selectedCountry.iso === "IN" && !INDIAN_PHONE_REGEX.test(rawPhone)) {
      return toast.error("Enter a valid 10-digit Indian mobile number");
    }
    if (selectedCountry.iso !== "IN" && !PHONE_REGEX.test(rawPhone)) {
      return toast.error("Enter a valid phone number");
    }

    if (!form.email.trim()) return toast.error("Enter email");
    if (!form.password.trim()) return toast.error("Enter password");

    if (form.password !== form.confirm) {
      return toast.error("Passwords do not match");
    }

    setLoading(true);

    try {
      const { data } = await memberApi.post("/member/register", {
        name: form.name,
        phone: rawPhone,
        email: form.email,
        password: form.password,
      });

      localStorage.setItem("ospf_member_token", data.token);
      saveMemberDraftProfile({
        name: form.name.trim(),
        phone: rawPhone,
        email: form.email.trim().toLowerCase(),
        country_code: selectedCountry.iso,
        phone_country_code: selectedCountry.dial,
        country_name: selectedCountry.country,
      });

      toast.success("Account created successfully");

      nav("/membership", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Password Login
  // =========================
  const handleLogin = async () => {
    if (!form.email.trim()) return toast.error("Enter email");
    if (!form.password.trim()) return toast.error("Enter password");

    setLoading(true);

    try {
      // memberLogin() internally fetches /members/me and calls setMember()
      const res = await memberLogin(form.email, form.password);

      if (!res?.ok) {
        toast.error(res?.error || "Login failed");
        return;
      }

      toast.success("Welcome back");

      // Use the member already fetched by memberLogin — no extra API call
      const loggedInMember = res.member;
      if (loggedInMember) {
        saveMemberDraftProfile({
          name: loggedInMember.name || form.name.trim(),
          phone: loggedInMember.phone || form.phone.trim(),
          email: loggedInMember.email || form.email.trim().toLowerCase(),
        });
        routeMember(loggedInMember);
      } else {
        // Fallback: member wasn't in the response (shouldn't happen, but safe)
        routeMember({ membership_status: "approved" });
      }
    } catch (e) {
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === "register") {
      await handleRegister();
    } else {
      await handleLogin();
    }
  };

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
        <h1 className="text-3xl font-semibold text-center mb-6">
          {mode === "register" ? "Create Account" : "Member Sign In"}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-xl border border-brand/10 bg-brand/5 px-4 py-3 text-sm text-ink-soft">
            Existing members can sign in with password.
            New applicants can switch to apply mode and start the membership
            form.
          </div>

          {mode === "register" && (
            <>
              <input
                name="name"
                placeholder="Full Name"
                value={form.name}
                onChange={handleChange}
                className="input-premium"
              />

              <div className="grid grid-cols-[108px_minmax(0,1fr)] gap-3">
            <select
              value={selectedCountry.iso}
              onChange={(e) => {
                const next =
                  COUNTRY_PHONE_OPTIONS.find((opt) => opt.iso === e.target.value) ||
                  INDIA_COUNTRY_OPTION;
                setSelectedCountry(next);
              }}
              className="input-premium h-14"
            >
              {COUNTRY_PHONE_OPTIONS.map((option) => (
                <option key={option.iso} value={option.iso}>
                  {option.country} {option.dial}
                </option>
              ))}
            </select>
            <input
              name="phone"
              placeholder="Mobile Number"
              value={form.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");

                if (value.length <= 15) {
                  setForm((prev) => ({
                    ...prev,
                    phone: value,
                  }));
                }
              }}
              maxLength={15}
              className="input-premium"
            />
          </div>
            </>
          )}

          <input
            name="email"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                email: e.target.value.trim().toLowerCase(),
              }));
            }}
            className="input-premium"
          />

          <div className="relative">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              className="input-premium pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {mode === "register" && (
            <div className="relative">
              <input
                name="confirm"
                type={showConfirm ? "text" : "password"}
                placeholder="Re-enter Password"
                value={form.confirm}
                onChange={handleChange}
                className="input-premium pr-12"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition"
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}

            {loading
              ? "Please wait..."
              : mode === "register"
                ? "Create Account & Continue"
                : "Sign In"}
          </button>

          <p className="text-xs text-center text-ink-muted">
            New and draft applicants continue the membership form. Pending and
            approved members land on the member dashboard.
          </p>

        </form>

        <div className="text-center mt-5 text-sm">
          {mode === "register" ? (
            <>
              Already a member?{" "}
              <button
                onClick={() => setMode("login")}
                className="text-brand underline"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              New user?{" "}
              <button
                onClick={() => setMode("register")}
                className="text-brand underline"
              >
                Apply here
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
